import axios from "axios";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Prefer same-origin when the current host and the configured backend host share
// the same base domain (e.g., www.leadmato.com <-> leadmato.com). This avoids the
// apex<->www 308 redirect that strips cross-origin POST bodies + credentials, and
// eliminates CORS/cookie edge cases on production custom domains.
function resolveBackendUrl() {
  if (typeof window === "undefined" || !RAW_BACKEND_URL) return RAW_BACKEND_URL;
  try {
    const loc = window.location;
    const backendUrl = new URL(RAW_BACKEND_URL);
    const stripWww = (h) => h.replace(/^www\./i, "");
    // Collapse to same-origin ONLY when host AND port match (true same-origin —
    // e.g. apex<->www in prod). In local dev the frontend (:3000) and backend
    // (:8000) share the host but differ by port, so we must keep the configured
    // backend URL, not the page origin.
    if (
      stripWww(loc.hostname) === stripWww(backendUrl.hostname) &&
      (loc.port || "") === (backendUrl.port || "")
    ) {
      return loc.origin;
    }
  } catch (_) {
    // fall through to raw
  }
  return RAW_BACKEND_URL;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── Workspace identity on every request ─────────────────────────────────────
// Phase 5.8. Until now the client never STATED which workspace it believed it
// was in: it sent a cookie, the server resolved `active_workspace_id` from the
// user document, and whatever came back was accepted as correct. That makes a
// misroute indistinguishable from success — the exact failure mode behind the
// TeamTab cross-tenant delete, where the client asked to remove a member of A
// and the server obligingly removed them from B and answered `{"ok": true}`.
//
// So: say it out loud. `X-Workspace-Id` names the workspace this request is
// FOR. `backend/auth.py current_workspace` treats it as authoritative when the
// session can access it, and answers **409 `{workspace_mismatch}`** when it
// cannot — a loud, attributable failure instead of a silent 200 from the wrong
// tenant.
//
// Two consequences worth being deliberate about:
//
//  1. The header WINS over the stored preference. That is the point: it makes
//     the client's belief the thing that gets checked. It also means the value
//     must be updated BEFORE the request that depends on it — `switchWorkspace`
//     in AuthContext pins the target id before it POSTs, not after.
//  2. A caller may pin a DIFFERENT workspace per-request (`headers:
//     {"X-Workspace-Id": ...}`) and we leave it alone. TeamTab uses this to
//     mutate the tenant whose roster is actually on screen rather than whatever
//     the shell happens to be showing now.
let activeWorkspaceId = null;

/** Called by AuthContext whenever the resolved workspace changes. */
export function setActiveWorkspaceId(id) {
  activeWorkspaceId = id || null;
}

export function getActiveWorkspaceId() {
  return activeWorkspaceId;
}

const WS_HEADER = "X-Workspace-Id";

function hasExplicitWorkspaceHeader(config) {
  const h = config?.headers;
  if (!h) return false;
  try {
    if (typeof h.has === "function") return h.has(WS_HEADER);
  } catch (_) {}
  return Boolean(h[WS_HEADER] || h["x-workspace-id"]);
}

api.interceptors.request.use((config) => {
  // A caller that pinned the header meant it — never overwrite a deliberate
  // statement of intent with the ambient one.
  if (hasExplicitWorkspaceHeader(config)) return config;
  if (!activeWorkspaceId) return config;
  try {
    if (config.headers && typeof config.headers.set === "function") {
      config.headers.set(WS_HEADER, activeWorkspaceId);
    } else {
      config.headers = { ...(config.headers || {}), [WS_HEADER]: activeWorkspaceId };
    }
  } catch (_) {}
  return config;
});

// ── Workspace mismatch (409) ────────────────────────────────────────────────
// The server refused because we named a workspace this session cannot reach.
// The correct response is to RE-SYNC, never to retry: retrying re-sends the
// same wrong id and gets the same 409, forever. `isRetryableError` already
// excludes 4xx, so nothing in the app retries this by accident — this handler
// exists to make the re-sync happen automatically instead of leaving the user
// on a screen that will keep failing.
//
// Same two-guard shape as the reseat handler above, for the same reason: the
// re-sync issues its own requests, and a page mid-load has several requests in
// flight all carrying the same stale id. One mismatch → one re-sync.
const mismatchSubscribers = new Set();
const MISMATCH_DEDUPE_MS = 5000;
let mismatchResyncing = false;
let lastMismatchAt = 0;

/**
 * Subscribe to "the workspace we claimed is not available on this session".
 * `fn(requestedWorkspaceId)` may return a promise; further 409s are ignored
 * until it settles.
 */
export function onWorkspaceMismatch(fn) {
  mismatchSubscribers.add(fn);
  return () => mismatchSubscribers.delete(fn);
}

/** True for the 409 the workspace header produces — and nothing else. */
export function isWorkspaceMismatch(e) {
  const res = e?.response;
  return res?.status === 409 && res?.data?.detail?.workspace_mismatch === true;
}

// Exported for tests/instrumentation only.
export function __resetMismatchState() {
  mismatchResyncing = false;
  lastMismatchAt = 0;
}

function handleMismatch(res) {
  if (res?.status !== 409) return;
  const detail = res?.data?.detail;
  if (!detail || detail.workspace_mismatch !== true) return;
  if (mismatchResyncing) return;
  const now = Date.now();
  if (now - lastMismatchAt < MISMATCH_DEDUPE_MS) return;
  lastMismatchAt = now;

  // eslint-disable-next-line no-console
  console.warn(
    "[workspace] mismatch — this session cannot reach",
    detail.requested_workspace_id,
    "— re-syncing"
  );
  if (!mismatchSubscribers.size) return;
  mismatchResyncing = true;
  const done = [];
  for (const fn of mismatchSubscribers) {
    try { done.push(fn(detail.requested_workspace_id)); } catch (_) {}
  }
  Promise.allSettled(done).finally(() => { mismatchResyncing = false; });
}

// ── Free liveness signal ────────────────────────────────────────────────────
// Spec §3.5: any API call returning 2xx proves the backend is demonstrably up.
// That is a recovery trigger we get for nothing, and every request already
// flows through this one interceptor. AuthContext subscribes while the
// workspace is unreachable and uses it to break out of the ladder early.
//
// Subscribers must be cheap and must self-throttle — this fires on EVERY
// successful response.
const apiOkSubscribers = new Set();

export function onApiSuccess(fn) {
  apiOkSubscribers.add(fn);
  return () => apiOkSubscribers.delete(fn);
}

// ── Workspace reseat signal ─────────────────────────────────────────────────
// `current_workspace` (backend/auth.py) heals a stale `active_workspace_id` —
// the user was removed from the workspace they were last in — by seating them
// in a fallback workspace, persisting it, and announcing it:
//
//     X-Workspace-Reseated: <the workspace_id they lost access to>
//
// Until something reads that, the shell keeps rendering the name of a workspace
// the user is no longer in while every request reads and writes a different one.
// That is the defect. We react HERE, in the one interceptor every response
// already passes through, so no caller has to know this exists.
//
// This is a sibling of `onApiSuccess`, not a use of it, on purpose. That hook
// is a liveness ping: it fires on every 2xx, carries only a url, and pushes
// throttling onto its subscribers. A reseat is a rare, identified, at-most-once
// event that needs the response HEADERS and must not make the liveness path
// think about de-duplication. Same interceptor, same subscriber-set shape,
// distinct semantics.
const reseatSubscribers = new Set();

// Two independent guards against the failure mode that matters: the re-sync we
// trigger issues its own requests, and must not be able to re-trigger itself.
//
//  1. `reseatResyncing` — while a re-sync is in flight the header is ignored
//     outright. The backend has already persisted the heal, so the re-sync's own
//     GET /workspaces/current comes back clean; this makes that structural
//     rather than a thing we hope about.
//  2. `seenReseats` — de-dupes on the workspace id that went away. A page
//     mid-load has several requests in flight and EVERY one of them can carry
//     the header (they all read the stale preference before the heal landed).
//     One reseat must produce one re-sync, not six.
//
// The cooldown, rather than a permanent Set: being reseated out of the same
// workspace twice is legitimate (rejoin a team, get removed again) and should
// re-sync again. It only has to outlive one page's in-flight batch.
const RESEAT_DEDUPE_MS = 30000;
const seenReseats = new Map(); // reseated_from -> timestamp
let reseatResyncing = false;

/**
 * Subscribe to "the server moved you to a different workspace".
 *
 * `fn(reseatedFromWorkspaceId)` may return a promise; the header stays ignored
 * until it settles, which is guard (1) above. Fires at most once per reseat.
 */
export function onWorkspaceReseated(fn) {
  reseatSubscribers.add(fn);
  return () => reseatSubscribers.delete(fn);
}

function readReseatHeader(res) {
  const h = res?.headers;
  if (!h) return null;
  try {
    if (typeof h.get === "function") return h.get("x-workspace-reseated") || null;
  } catch (_) {}
  return h["x-workspace-reseated"] || h["X-Workspace-Reseated"] || null;
}

// Exported for tests/instrumentation only — nothing in the app should call it.
export function __resetReseatState() {
  seenReseats.clear();
  reseatResyncing = false;
}

function handleReseat(res) {
  const from = readReseatHeader(res);
  if (!from) return;
  if (reseatResyncing) return; // guard 1 — no loop

  const now = Date.now();
  for (const [id, at] of seenReseats) {
    if (now - at > RESEAT_DEDUPE_MS) seenReseats.delete(id);
  }
  if (seenReseats.has(from)) return; // guard 2 — once per reseat
  seenReseats.set(from, now);

  if (!reseatSubscribers.size) return;
  reseatResyncing = true;
  const done = [];
  for (const fn of reseatSubscribers) {
    try { done.push(fn(from)); } catch (_) {}
  }
  Promise.allSettled(done).finally(() => { reseatResyncing = false; });
}

api.interceptors.response.use(
  (r) => {
    // The reseat happens inside the `current_workspace` dependency, so the
    // header rides on whatever response that request produced — read it before
    // anything else can throw.
    try { handleReseat(r); } catch (_) {}
    if (apiOkSubscribers.size) {
      for (const fn of apiOkSubscribers) {
        try { fn(r?.config?.url); } catch (_) {}
      }
    }
    return r;
  },
  (err) => {
    // A reseat can land on a FAILED response too: the dependency reseats, then
    // the handler 404s. The header is already on the wire; a 4xx is no reason to
    // keep rendering a workspace the user was just moved out of.
    try { handleReseat(err?.response); } catch (_) {}
    // 409 workspace_mismatch → re-sync, never retry (see handleMismatch).
    try { handleMismatch(err?.response); } catch (_) {}
    // 401 → let ProtectedRoute redirect
    return Promise.reject(err);
  }
);

/**
 * Is this error worth retrying? Spec §2: network errors and 5xx ONLY.
 * A 401/403 is not a retry case — that belongs to ProtectedRoute, and
 * hammering it five times just delays the redirect the user needs.
 */
export function isRetryableError(e) {
  const status = e?.response?.status;
  if (status === undefined) return true; // no response at all → network/timeout
  return status >= 500 && status <= 599;
}

/**
 * A short, stable-ish reference code for a failure: `WS-500-7f3a`.
 * Owner decision 4 — show it, small and grey. It ships UNLABELLED: captioning
 * it "Error" adds a red word to an amber banner. Support asks for "the small
 * grey code". The server's own `detail` never reaches a screen.
 */
export function referenceCode(e, prefix = "WS") {
  const status = e?.response?.status || "NET";
  let seed = "";
  try {
    seed = `${e?.config?.url || ""}|${e?.message || ""}|${Math.floor(Date.now() / 60000)}`;
  } catch (_) {
    seed = String(Math.random());
  }
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return `${prefix}-${status}-${(h >>> 0).toString(16).slice(0, 4).padStart(4, "0")}`;
}

// Some endpoints raise a STRUCTURED error detail (e.g. the brand-analysis 24h
// rate limit: {rate_limited, feature, unblocks_at, retry_after_seconds, message}
// or the paywall's {error, feature, message}) instead of a plain string. Passing
// that object straight to toast.error()/JSX crashes React ("Objects are not
// valid as a React child"). Always read the error through this helper.
export function getErrorMessage(e, fallback = "Something went wrong. Please try again.") {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
}

export default api;
